import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Leaf, Award, Users, Factory, MapPin, ArrowRight } from "lucide-react";

export default function AboutPage() {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 md:py-32 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1 bg-accent text-primary font-medium text-sm rounded-full mb-6">
            Our Story
          </span>
          <h1 className="font-heading text-4xl md:text-6xl text-white font-bold mb-6" data-testid="about-title">
            25 Years of
            <br />
            Cotton Excellence
          </h1>
          <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto">
            From Tirupur, the heart of India's textile industry, OGAS brings you
            premium quality cotton t-shirts since 1999.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                The OGAS Journey
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                Founded in 1999 in Tirupur, Tamil Nadu, OGAS started with a simple
                vision: to create the most comfortable cotton t-shirts in India. What
                began as a small manufacturing unit has grown into a trusted brand
                serving thousands of customers nationwide.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                Our founder believed that quality should never be compromised. That
                belief still drives everything we do today. From selecting the finest
                100% cotton to our proprietary bio-wash process, every step is designed
                to deliver comfort you can feel.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Today, OGAS operates with two distinct brands: <strong>U & Mi</strong> for
                men's fashion and <strong>KiTees</strong> for boys' comfortable wear. Both
                carry our promise of quality, comfort, and style.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1741275269921-3c562cdb9176?w=600"
                alt="OGAS Manufacturing"
                className="rounded-lg shadow-hover w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-cotton-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "25+", label: "Years Experience" },
              { number: "50K+", label: "Happy Customers" },
              { number: "100%", label: "Cotton Quality" },
              { number: "2", label: "Trusted Brands" },
            ].map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="font-heading text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stat.number}
                </div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              What We Stand For
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Our core values guide every decision we make
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Leaf className="w-8 h-8" />,
                title: "Pure Cotton",
                description:
                  "We use only 100% cotton with bio-wash finish for maximum comfort and safety.",
              },
              {
                icon: <Award className="w-8 h-8" />,
                title: "Quality First",
                description:
                  "Every piece goes through rigorous quality checks before reaching you.",
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: "Expert Team",
                description:
                  "Our experienced staff and workers bring decades of expertise.",
              },
              {
                icon: <Factory className="w-8 h-8" />,
                title: "Own Manufacturing",
                description:
                  "Complete control over production ensures consistent quality.",
              },
            ].map((value, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-soft text-center"
                data-testid={`value-${index}`}
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mx-auto mb-4">
                  {value.icon}
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">{value.title}</h3>
                <p className="text-muted-foreground">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="py-16 md:py-24 bg-cotton-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Our Brands
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-brand-mens p-8 rounded-lg text-white" data-testid="brand-umi">
              <h3 className="font-heading text-3xl font-bold mb-4">U & Mi</h3>
              <p className="text-white/80 mb-6">
                Premium cotton t-shirts designed for the modern Indian man. Classic
                styles, contemporary fits, and timeless comfort. U & Mi represents
                sophistication meets everyday wearability.
              </p>
              <Button
                asChild
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-brand-mens rounded-full"
              >
                <Link to="/products/mens">
                  Shop U & Mi <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>

            <div className="bg-brand-boys p-8 rounded-lg text-white" data-testid="brand-kitees">
              <h3 className="font-heading text-3xl font-bold mb-4">KiTees</h3>
              <p className="text-white/80 mb-6">
                Fun, vibrant, and incredibly comfortable t-shirts for active boys.
                KiTees is all about letting kids be kids while staying comfortable
                and stylish. Colors that pop, fabric that lasts.
              </p>
              <Button
                asChild
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-brand-boys rounded-full"
              >
                <Link to="/products/boys">
                  Shop KiTees <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 text-primary mb-4">
                <MapPin className="w-5 h-5" />
                <span className="font-medium">Our Location</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Made in Tirupur
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                Tirupur, Tamil Nadu is known as the "Knit Wear Capital of India" and
                is home to our manufacturing unit. Being in the heart of India's
                textile hub gives us access to the finest cotton and skilled
                craftspeople.
              </p>
              <div className="bg-cotton-100 p-6 rounded-lg">
                <h4 className="font-bold mb-2">OGAS Manufacturing Unit</h4>
                <p className="text-muted-foreground">
                  25A, Padmavathipuram 1st Street,<br />
                  Gandhinagar Post, Tirupur - 641603<br />
                  Tamil Nadu, India
                </p>
                <p className="text-muted-foreground mt-2">
                  <strong>Phone:</strong>{" "}
                  <a href="tel:+919994793643" className="text-primary hover:underline">
                    +91 99947 93643
                  </a>
                </p>
                <p className="text-muted-foreground mt-1">
                  <strong>Email:</strong>{" "}
                  <a href="mailto:admin@ogas.in" className="text-primary hover:underline">
                    admin@ogas.in
                  </a>
                </p>
                <p className="text-muted-foreground mt-1">
                  <strong>Website:</strong>{" "}
                  <a
                    href="https://www.ogas.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    www.ogas.in
                  </a>
                </p>
              </div>
            </div>
            <div className="aspect-video rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d125323.10892108671!2d77.2692862!3d11.1085241!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba907b0c1a0e5f7%3A0x3a3f7f57e5f68d0a!2sTirupur%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1635000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="OGAS Location"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white font-bold mb-4">
            Experience the OGAS Difference
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers. Shop our collection of premium
            cotton t-shirts today.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-accent text-primary hover:bg-accent/90 rounded-full font-semibold"
            data-testid="shop-now-cta"
          >
            <Link to="/products">
              Shop Now <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
